import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import butterflyApi from '../ButterflyApi';
import MobilePage from '../UiComponents/MobilePage';
import { callName } from '../Utils/Strings';
import { replaceAppTitleCallName } from './appProductionStatus';

const APP_PAYMENT_ORDER_ID_KEY = 'appPaymentOrderId';
const APP_PAYMENT_HERO_NO_KEY = 'appPaymentHeroNo';

const PAYMENT_STATUS_TEXT = {
  READY: '결제 대기',
  APPROVED: '결제 완료',
  CANCELED: '결제 취소',
  FAILED: '결제 실패',
}

const formatPrice = (price) => {
  if (price === undefined || price === null) return ''
  return `${new Intl.NumberFormat('ko-KR').format(price)}원`
}

const getDiscountedPrice = (app) => {
  if (!app || app.salePrice === undefined || app.salePrice === null) return
  return Math.max(app.salePrice - (app.discountPrice || 0), 0)
}

const formatDate = (datetime) => {
  if (!datetime) return '-'
  return datetime.replace('T', ' ')
}

const getSearchOrderId = (searchParams) => {
  return searchParams.get('orderId')
    || searchParams.get('order_id')
    || searchParams.get('OrderId')
    || searchParams.get('orderID')
}

const getSearchHeroNo = (searchParams) => {
  return searchParams.get('heroNo')
    || searchParams.get('hero_no')
    || searchParams.get('HeroNo')
}

const getStoredOrderId = () => {
  try {
    return sessionStorage.getItem(APP_PAYMENT_ORDER_ID_KEY)
  } catch (e) {
    return ''
  }
}

const getStoredHeroNo = () => {
  try {
    return sessionStorage.getItem(APP_PAYMENT_HERO_NO_KEY)
  } catch (e) {
    return ''
  }
}

const getLocalHeroes = () => {
  try {
    return JSON.parse(localStorage.getItem('heroes')) || []
  } catch (e) {
    return []
  }
}

const replaceCallName = (text, heroCallName) => {
  return (text || '').replace(/\{callName\}/g, heroCallName)
}

const AppPaymentComplete = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState()
  const [loading, setLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState('')

  const orderId = getSearchOrderId(searchParams) || getStoredOrderId()
  const heroNo = getSearchHeroNo(searchParams) || getStoredHeroNo()
  const app = order && order.app
  const hero = getLocalHeroes().find(hero => `${hero.heroNo}` === `${heroNo || order?.heroNo}`)
  const heroCallName = hero ? callName(hero.firstName) : '하늘이'
  const appWithProductionStatus = app && {
    ...app,
    productionStatus: app.productionStatus || order?.productionStatus,
    resultUrl: app.resultUrl || order?.resultUrl,
  }
  const appTitle = app && replaceAppTitleCallName(app.title || app.appName, appWithProductionStatus, heroCallName)
  const appDescription = app && replaceCallName(app.description, heroCallName)
  const paymentStatusText = order && (PAYMENT_STATUS_TEXT[order.paymentStatus] || order.paymentStatus)
  const paymentApproved = order && order.paymentStatus === 'APPROVED'

  const handleApiError = useCallback((error) => {
    const message = (error.response && error.response.data && error.response.data.message) || error.message
    setErrorMessage(message || '주문 정보를 불러오지 못했습니다.')
  }, [])

  const loadOrder = useCallback(async () => {
    if (!orderId) {
      setLoading(false)
      setErrorMessage('주문번호를 확인할 수 없습니다.')
      return
    }

    setLoading(true)
    setErrorMessage('')
    const response = await butterflyApi.getAppPaymentOrder(orderId, heroNo, handleApiError)
    setOrder(response)
    setLoading(false)
  }, [orderId, heroNo, handleApiError])

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 })
    loadOrder()
  }, [loadOrder])

  if (loading) {
    return <MobilePage loading loadingMessage="결제 정보를 확인하고 있습니다." />
  }

  return <MobilePage>
    <div className='px-4 pt-24 pb-16'>
      <div className='text-center'>
        <div className={`mx-auto flex h-16 w-16 items-center justify-center rounded-full ${paymentApproved ? 'bg-green-50 text-new-green' : 'bg-gray-100 text-gray-500'}`}>
          <span className='text-3xl font-bold'>{paymentApproved ? '✓' : '!'}</span>
        </div>
        <h1 className='mt-4 font-jua text-3xl text-text-main'>
          {paymentApproved ? '결제가 완료되었습니다' : '결제 정보를 확인해 주세요'}
        </h1>
        <p className='mt-2 text-sm text-text-sub'>
          주문 정보를 기준으로 결제 내역을 표시합니다.
        </p>
      </div>

      {
        errorMessage && <div className='mt-8 rounded-lg border border-red-100 bg-red-50 p-4 text-sm text-red-600'>
          {errorMessage}
        </div>
      }

      {
        order && <div className='mt-8 space-y-4'>
          <div className='rounded-lg bg-white p-4 shadow-[rgba(0,0,15,0.08)_0_4px_12px_0px]'>
            <div className='flex items-start justify-between gap-3'>
              <div>
                <div className='text-xs text-text-sub'>결제 상태</div>
                <div className='mt-1 font-bold text-text-main'>{paymentStatusText}</div>
              </div>
              <div className='rounded-full bg-green-50 px-3 py-1 text-[11px] font-bold text-new-green'>
                {order.paymentStatus}
              </div>
            </div>
            <div className='mt-5 space-y-3 border-t border-gray-100 pt-4 text-sm'>
              <div className='flex justify-between gap-4'>
                <span className='text-text-sub'>주문번호</span>
                <span className='text-right font-bold text-text-main'>{order.orderId}</span>
              </div>
              <div className='flex justify-between gap-4'>
                <span className='text-text-sub'>결제금액</span>
                <span className='font-bold text-text-main'>{formatPrice(order.amount)}</span>
              </div>
              <div className='flex justify-between gap-4'>
                <span className='text-text-sub'>결제일시</span>
                <span className='text-right text-text-main'>{formatDate(order.paymentDatetime)}</span>
              </div>
            </div>
          </div>

          {
            app && <div className='rounded-lg bg-white p-4 shadow-[rgba(0,0,15,0.08)_0_4px_12px_0px]'>
              <div className='flex gap-4'>
                { 
                  app.imageUrl && <div className='h-24 w-24 flex-shrink-0 overflow-hidden rounded-lg bg-new-white'>
                    <img className='h-full w-full object-cover' src={app.imageUrl} alt={appTitle} />
                </div>
                }
                <div className='min-w-0 flex-1'>
                  <div className='font-jua text-xl leading-tight text-text-main'>{appTitle}</div>
                  <div className='mt-2 text-xs leading-relaxed text-text-sub'>{appDescription}</div>
                  <div className='mt-3 inline-flex rounded-full bg-background-floor px-3 py-1 text-[11px] font-bold text-text-sub'>
                    {app.category}
                  </div>
                </div>
              </div>
              <div className='mt-4 grid grid-cols-3 gap-2 border-t border-gray-100 pt-4 text-center'>
                <div>
                  <div className='text-[11px] text-text-sub'>판매가</div>
                  <div className='mt-1 text-xs text-gray-400 line-through'>{formatPrice(app.salePrice)}</div>
                </div>
                <div>
                  <div className='text-[11px] text-text-sub'>할인</div>
                  <div className='mt-1 text-xs font-bold text-new-green'>-{formatPrice(app.discountPrice)}</div>
                </div>
                <div>
                  <div className='text-[11px] text-text-sub'>최종 할인가</div>
                  <div className='mt-1 text-sm font-bold text-text-main'>{formatPrice(getDiscountedPrice(app))}</div>
                </div>
              </div>
            </div>
          }

          <div className='grid grid-cols-2 gap-2'>
            <button
              type='button'
              className='rounded-lg border border-new-green bg-white px-4 py-3 text-sm font-bold text-new-green'
              onClick={() => navigate('/')}
            >
              홈으로
            </button>
            <button
              type='button'
              className='rounded-lg bg-new-green px-4 py-3 text-sm font-bold text-white'
              onClick={() => navigate('/orders')}
            >
              주문내역 보기
            </button>
          </div>
        </div>
      }
    </div>
  </MobilePage>
}

export default AppPaymentComplete
